import { createContext, PropsWithChildren, useReducer } from "react";

import { create } from "@bufbuild/protobuf";

import { SourceType } from "@gx/ts-contracts/services/database/models/public_pb";
import {
  CreateProjectRequestSchema,
  CreateProjectSourcesRequest,
  UpdateProjectRequest,
} from "@gx/ts-contracts/services/ontology/api/project_pb";
import { ProjectMappingMethod } from "@gx/ts-contracts/services/project/resources/project_configuration/project_configuration_pb";

import { CreateProjectFormAction } from "@/pages/projects/forms/create/actions";
import {
  CREATE_PROJECT_FORM_STEPS,
  PROJECT_SYNC_NONE_CRON_EXPRESSION,
} from "@/pages/projects/forms/create/constants";
import createProjectFormReducer from "@/pages/projects/forms/create/reducer";
import { CreateProjectFormState, CreateProjectFormStep } from "@/pages/projects/forms/create/types";
import { ALL_SOURCES } from "@/pages/sources/components/create/constants";
import { FILTERABLE_SOURCE_STATUSES } from "@/pages/sources/constants";

export type CreateProjectFormContextShape = {
  state: CreateProjectFormState;
  dispatch: React.Dispatch<CreateProjectFormAction>;
};

export const DEFAULT_STATE: CreateProjectFormState = {
  search: "",
  step: CreateProjectFormStep.SELECT_PROJECT_SOURCES,
  navigation: {
    currentStepIndex: 0,
    totalSteps: CREATE_PROJECT_FORM_STEPS.length,
    isFirstStep: true,
    isLastStep: false,
    canGoBack: false,
    canGoForward: false,
  },
  isLoading: false,
  isUpdateProjectSucceeded: false,
  shouldShowErrors: false,
  updateProjectRequest: {} as UpdateProjectRequest,
  createProjectSourcesRequest: {} as CreateProjectSourcesRequest,
  selectedSourceTypes: ALL_SOURCES.filter((s) => s !== SourceType.UNSPECIFIED),
  selectedStatuses: FILTERABLE_SOURCE_STATUSES,
  selectedMappingMethod: ProjectMappingMethod.AUTO,
  autoIntrospectPrompt: "",
  projectSyncCronExpression: PROJECT_SYNC_NONE_CRON_EXPRESSION,
  createProjectRequest: create(CreateProjectRequestSchema, {}),
};

const DEFAULT_CONTEXT: CreateProjectFormContextShape = {
  state: DEFAULT_STATE,
  dispatch: () => undefined,
};

export const CreateProjectFormContext =
  createContext<CreateProjectFormContextShape>(DEFAULT_CONTEXT);
CreateProjectFormContext.displayName = "CreateProjectFormContext";

const CreateProjectFormProvider = ({
  children,
  initialState,
}: PropsWithChildren<{
  initialState?: CreateProjectFormState;
}>) => {
  const state = initialState ?? DEFAULT_STATE;
  const [currentState, dispatch] = useReducer(createProjectFormReducer, state);

  return (
    <CreateProjectFormContext.Provider value={{ state: currentState, dispatch }}>
      {children}
    </CreateProjectFormContext.Provider>
  );
};

export default CreateProjectFormProvider;
